import { useState, useCallback, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Box,
    CircularProgress,
    Typography,
    Paper,
    Button,
    useTheme,
    Alert,
    AlertTitle,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import { useMsal } from "@azure/msal-react";
import Editor from "@monaco-editor/react";
import FormPanelHeader from "../molecules/FormPanelHeader";
import PageMetadata from "../molecules/PageMetadata";
import FormDataEntry from "./TaxReturnWebforms/FormDataEntry";
import { AppDispatch, RootState } from "../../configureStore";
import { ExtractTask, ReturnActive, FormExtractions, TaxReturnPage } from "../../interface/returns";
import { UPDATE_EXTRACTED_FIELD_VALUE } from "../../redux/reducer/returns";
import { ExtractedFormResult } from "../../interface/extracted";
import { developerList } from "../../utils";
import useIsMobile from "../../hooks/useIsMobile";

interface FormPanelContentProps {
    selectedFormType: string;
    currentFormData: any;
    currentPageData: ExtractedFormResult<any> | null;
    currentPageIndex: number;
    currentExtractionIndex: number;
    filteredPages: TaxReturnPage[];
    isCurrentPageRemoved: boolean;
    onAdvancePage: () => void;
    currentPageInstances?: { instance: ExtractedFormResult<any>; index: number }[];
    currentInstanceIndex?: number;
    onInstanceIndexChange?: (index: number) => void;
}

const getInstanceLabel = (instance: ExtractedFormResult<any> | undefined, fallbackIndex: number): string => {
    const data: any = instance?.extractedData?.data;
    const ssn = data?.employeeInfo?.ssn;
    if (typeof ssn === "string" && ssn.trim()) {
        const last4 = ssn.replace(/\D/g, "").slice(-4);
        if (last4.length === 4) return `···${last4}`;
    }
    const first = data?.employeeInfo?.firstName;
    const last = data?.employeeInfo?.lastName;
    if (first || last) return [first, last].filter(Boolean).join(" ");
    return `${fallbackIndex + 1}`;
};

const FormPanelContent = ({
    selectedFormType,
    currentFormData,
    currentPageData,
    currentPageIndex,
    currentExtractionIndex,
    filteredPages,
    isCurrentPageRemoved,
    onAdvancePage,
    currentPageInstances = [],
    currentInstanceIndex = 0,
    onInstanceIndexChange,
}: FormPanelContentProps) => {
    const isMobile = useIsMobile();
    const dispatch = useDispatch<AppDispatch>();
    const theme = useTheme();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Reset scroll position when page or instance changes
    useEffect(() => {
        scrollContainerRef.current?.scrollTo(0, 0);
    }, [currentPageIndex, currentInstanceIndex]);
    const activeReturnJson = useSelector<RootState, FormExtractions | undefined>(
        ({ returns }) => returns.extractedJson,
    );
    const activeReturnTask = useSelector<RootState, ReturnActive | undefined>(
        ({ returns }) => returns.activeReturnTask,
    );
    const extractTask = activeReturnTask?.type === "extract" ? (activeReturnTask as ExtractTask) : undefined;
    const { accounts } = useMsal();
    const [viewMode, setViewMode] = useState<"form" | "json">("form");
    const [dismissedErrors, setDismissedErrors] = useState<Set<string>>(new Set());

    const isDeveloper = accounts[0] && developerList.includes(accounts[0].username.toLowerCase());

    const handleViewModeChange = useCallback((_: React.MouseEvent<HTMLElement>, newMode: "form" | "json" | null) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    }, []);

    // Check if showOnlyImportant is needed for any conditional rendering
    useSelector<RootState, boolean>(({ ui }) => ui.showOnlyImportantFields);

    const handleToggleRemovePage = useCallback(() => {
        if (!selectedFormType || !currentFormData) return;

        // If there's an existing extraction entry, toggle its removed state
        if (currentExtractionIndex !== -1 && currentFormData.extraction) {
            const currentRemoved = currentFormData.extraction[currentExtractionIndex]?.removed === true;
            dispatch({
                type: UPDATE_EXTRACTED_FIELD_VALUE,
                value: !currentRemoved,
                location: `${selectedFormType}.extraction[${currentExtractionIndex}].removed`,
            });
            // Auto-advance to next page when removing (not restoring)
            if (!currentRemoved && currentPageIndex < filteredPages.length - 1) {
                onAdvancePage();
            }
            return;
        }

        // No extraction entry exists — create a stub entry with removed: true
        const currentPage = filteredPages[currentPageIndex];
        if (!currentPage) return;
        const newIndex = currentFormData.extraction?.length || 0;
        dispatch({
            type: UPDATE_EXTRACTED_FIELD_VALUE,
            value: {
                version: "0.0.1",
                type: selectedFormType,
                pageNumber: currentPage.form?.page || 1,
                overallConfidence: 0,
                requiresManualReview: false,
                extractedData: {
                    page: currentPage.pageNumber,
                    originalPage: currentPage.pageNumber,
                    formPageNumber: currentPage.form?.page || 1,
                    taxYear: 0,
                    data: {},
                },
                fieldConfidence: {},
                extractionMetadata: {
                    documentQuality: "poor",
                    scanQuality: "low",
                    extractionNotes: "Stub entry created for removal",
                },
                removed: true,
            },
            location: `${selectedFormType}.extraction[${newIndex}]`,
        });
        // Auto-advance to next page after removing
        if (currentPageIndex < filteredPages.length - 1) {
            onAdvancePage();
        }
    }, [
        selectedFormType,
        currentFormData,
        currentExtractionIndex,
        currentPageIndex,
        filteredPages,
        dispatch,
        onAdvancePage,
    ]);

    return (
        <>
            <FormPanelHeader
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                isDeveloper={isDeveloper}
            />

            <Paper
                sx={{
                    p: isMobile ? 1 : 2,
                    pb: 0,
                    pr: 0.5,
                    backgroundColor: (t) =>
                        t.palette.mode === "dark" ? t.palette.background.paper : t.palette.background.paper,
                    overflow: "auto",
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                }}
            >
                <Box
                    sx={{
                        overflow: "hidden",
                        flex: 1,
                        mb: 1,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {viewMode === "json" ? (
                        <Box sx={{ flex: 1, minHeight: 0 }}>
                            <Editor
                                height="100%"
                                language="json"
                                theme={theme.palette.mode === "dark" ? "vs-dark" : "light"}
                                value={JSON.stringify(
                                    selectedFormType && currentFormData
                                        ? { [selectedFormType]: currentFormData }
                                        : activeReturnJson,
                                    null,
                                    2,
                                )}
                                options={{
                                    readOnly: true,
                                    fontSize: 13,
                                    fontFamily: "'Fira Code', 'Consolas', 'Monaco', monospace",
                                    minimap: { enabled: false },
                                    lineNumbers: "on",
                                    wordWrap: "on",
                                    scrollBeyondLastLine: false,
                                    automaticLayout: true,
                                    tabSize: 2,
                                    folding: true,
                                    foldingHighlight: true,
                                    showFoldingControls: "mouseover",
                                }}
                                loading={
                                    <Box
                                        sx={{
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            height: "100%",
                                        }}
                                    >
                                        <CircularProgress size={24} />
                                    </Box>
                                }
                            />
                        </Box>
                    ) : (
                        <Box ref={scrollContainerRef} sx={{ overflow: "auto", flex: 1, pr: 0.5 }}>
                            {selectedFormType && currentFormData ? (
                                <Box>
                                    {/* Show extraction status */}
                                    {currentFormData.error && !dismissedErrors.has(selectedFormType) && (
                                        <Alert
                                            severity="error"
                                            onClose={() => {
                                                setDismissedErrors((prev) => new Set(prev).add(selectedFormType));
                                            }}
                                            sx={{ mb: 2 }}
                                        >
                                            <AlertTitle>Extraction Error</AlertTitle>
                                            {currentFormData.error}
                                        </Alert>
                                    )}

                                    {/* Page metadata */}
                                    <PageMetadata
                                        currentPageData={currentPageData}
                                        currentFormData={currentFormData}
                                        isCurrentPageRemoved={isCurrentPageRemoved}
                                        currentPageIndex={currentPageIndex}
                                        filteredPages={filteredPages}
                                        selectedFormType={selectedFormType}
                                        onToggleRemovePage={handleToggleRemovePage}
                                    />

                                    {/* Page removed alert */}
                                    {isCurrentPageRemoved && (
                                        <Alert
                                            severity="warning"
                                            sx={{ mb: 2 }}
                                            action={
                                                <Button
                                                    color="inherit"
                                                    size="small"
                                                    onClick={handleToggleRemovePage}
                                                >
                                                    Restore
                                                </Button>
                                            }
                                        >
                                            This page has been removed from extraction and will be excluded from
                                            grouping.
                                        </Alert>
                                    )}

                                    {/* Instance selector — shown when the current PDF page contains
                                        multiple form instances (e.g. two W-2s on a single page). */}
                                    {currentPageInstances.length > 1 && onInstanceIndexChange && (
                                        <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                {selectedFormType.toUpperCase()} on this page:
                                            </Typography>
                                            <ToggleButtonGroup
                                                size="small"
                                                exclusive
                                                value={currentInstanceIndex}
                                                onChange={(_, value) => {
                                                    if (typeof value === "number") onInstanceIndexChange(value);
                                                }}
                                            >
                                                {currentPageInstances.map(({ instance }, i) => (
                                                    <ToggleButton
                                                         
                                                        key={`${instance.formIndexOnPage ?? i + 1}-${i}`}
                                                        value={i}
                                                    >
                                                        {`${i + 1} of ${currentPageInstances.length}`}
                                                        <Typography
                                                            component="span"
                                                            variant="caption"
                                                            sx={{ ml: 0.75, opacity: 0.75 }}
                                                        >
                                                            {getInstanceLabel(instance, i)}
                                                        </Typography>
                                                    </ToggleButton>
                                                ))}
                                            </ToggleButtonGroup>
                                        </Box>
                                    )}

                                    {/* Form fields rendered using generic FormDataEntry */}
                                    <Box
                                        sx={isCurrentPageRemoved ? { opacity: 0.4, pointerEvents: "none" } : undefined}
                                    >
                                        <FormDataEntry
                                            key={`${selectedFormType}-${filteredPages[currentPageIndex]?.pageNumber ?? 0}-${currentPageData?.formIndexOnPage ?? currentInstanceIndex + 1}`}
                                            formData={currentFormData as any}
                                            originalPage={filteredPages[currentPageIndex]?.pageNumber ?? 0}
                                            formType={selectedFormType}
                                            formIndexOnPage={
                                                currentPageData?.formIndexOnPage ?? currentInstanceIndex + 1
                                            }
                                        />
                                    </Box>
                                </Box>
                            ) : (
                                <Box>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{ mb: 2 }}
                                    >
                                        Select a form type to begin data entry.
                                    </Typography>

                                    {/* Show summary of available forms */}
                                    {extractTask?.blobs?.extractedJson && (
                                        <Box sx={{ mt: 3 }}>
                                            <Typography
                                                variant="subtitle2"
                                                sx={{ fontWeight: 600, mb: 1 }}
                                            >
                                                Available Forms:
                                            </Typography>
                                            {Object.entries(activeReturnJson ?? {}).map(([formType, formData]) => (
                                                <Box
                                                    key={formType}
                                                    sx={{ ml: 2, mb: 1 }}
                                                >
                                                    <Typography variant="body2">
                                                        {formType.toUpperCase()}:{" "}
                                                        {formData.extraction
                                                            ? `${formData.extraction.length} page(s)`
                                                            : formData.error || "No data"}
                                                    </Typography>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Action buttons - fixed at bottom */}
            </Paper>
        </>
    );
};

export default FormPanelContent;
